import { Formik } from "formik";
import * as Yup from "yup";
import React, { useState } from "react";
import { Alert, Button, Col, Form, Input, InputGroup, Label, Row, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { saveAiVisibilityStore } from "../../../services/aiVisibilityApi";
import { persistSetupWebsiteId } from "../../../components/AiVisibility/websiteKeysUtils";

const PLATFORM_OPTIONS = [
	{ value: "shopify", label: "Shopify" },
	{ value: "bigcommerce", label: "BigCommerce" },
	{ value: "wordpress", label: "WordPress" },
	{ value: "drupal", label: "Drupal" },
	{ value: "magento", label: "Magento" },
	{ value: "others", label: "Others" },
];

const CollectAiVisibility = ({
	completePath = "/getStarted/aiVisibilityComplete",
}) => {
	const [errorMessages, setErrorMessages] = useState("");
	const navigate = useNavigate();

	const saveStoreInformation = async (values, actions) => {
		setErrorMessages("");
		const { ok, message, data, taskStatus } = await saveAiVisibilityStore(
			values.storeURL,
			values.storeType,
			{
				enable_web_search: true,
				prompt_count: 15,
			}
		);
		actions.setSubmitting(false);
		if (!ok) {
			setErrorMessages(message);
			return;
		}
		const website = data?.website;
		if (website?.websiteId != null) {
			// Do not set _website_id cookie here — GetStarted redirects when that cookie exists.
			persistSetupWebsiteId(website.websiteId);
		}
		if (message) toast.success(message);
		navigate(completePath, {
			state: {
				website: data?.website,
				websiteProductMapping: data?.websiteProductMapping,
				websiteKeys: data?.websiteKeys ?? [],
				audit: data?.audit,
				initialTaskStatus: taskStatus ?? data?.audit?.taskStatus,
			},
			replace: true,
		});
	};

	return (
		<>
			<section className="choosePlatformHeader">
				<div className="container">
					<Row className="align-items-center justify-content-center">
						<Col xs="auto">
							<h2 className="mt-5">AI Visibility Setup</h2>
							<p className="text-center text-muted">
								Step 1: Configure your website and audit settings
							</p>
						</Col>
					</Row>
				</div>
			</section>
			<section className="shopifyAppInstallationGuide mt-5">
				<div className="container">
					<Row className="align-items-center justify-content-center">
						<Col xs={12} lg={6} className="text-center d-none d-lg-block">
							<img
								src={require("./../../../assets/images/integration.png")}
								alt="integration"
								className="img-fluid"
							/>
						</Col>
						<Col xs={12} lg={6}>
							{errorMessages !== "" && <Alert color="danger">{errorMessages}</Alert>}
							<div className="storeInformationWrapper">
								<Formik
									initialValues={{
										storeURL: "",
										storeType: "",
									}}
									onSubmit={(values, actions) => {
										saveStoreInformation(values, actions);
									}}
									validationSchema={Yup.object().shape({
										storeURL: Yup.string()
											.required("Store URL is required")
											.url("Please enter a valid URL"),
										storeType: Yup.string().required("Platform is required"),
									})}
								>
									{(props) => {
										const {
											values,
											touched,
											errors,
											isSubmitting,
											handleChange,
											handleBlur,
											handleSubmit,
										} = props;
										return (
											<Form role="form" onSubmit={handleSubmit} noValidate>
												<div
													className={`position-relative mb-5 ${
														errors.storeType && touched.storeType ? "error_field" : ""
													}`}
												>
													<Label className="input-group-alternative-label">Platform</Label>
													<InputGroup className="input-group-alternative">
														<Input
															type="select"
															name="storeType"
															value={values.storeType}
															onChange={handleChange}
															onBlur={handleBlur}
															className="col"
														>
															<option value="">Select platform</option>
															{PLATFORM_OPTIONS.map((opt) => (
																<option key={opt.value} value={opt.value}>
																	{opt.label}
																</option>
															))}
														</Input>
													</InputGroup>
													{errors.storeType && touched.storeType && (
														<div className="input-feedback">{errors.storeType}</div>
													)}
												</div>
												<div
													className={`position-relative mb-5 ${
														errors.storeURL && touched.storeURL ? "error_field" : ""
													}`}
												>
													<Label className="input-group-alternative-label">Store URL</Label>
													<InputGroup className="input-group-alternative">
														<Input
															placeholder="https://www.example.com"
															type="text"
															name="storeURL"
															value={values.storeURL}
															onChange={handleChange}
															onBlur={handleBlur}
															className="col"
															autoComplete="off"
														/>
													</InputGroup>
													{errors.storeURL && touched.storeURL && (
														<div className="input-feedback">{errors.storeURL}</div>
													)}
												</div>
												<div className="d-flex justify-content-center">
													{isSubmitting ? (
														<Button className="cta authCta" color="primary" type="button" disabled>
															<Spinner className="white mr-1" size="sm" color="light" />
															<span className="ml-2 d-inline-block">Loading</span>
														</Button>
													) : (
														<Button className="cta authCta" color="primary" type="submit">
															Save
														</Button>
													)}
												</div>
											</Form>
										);
									}}
								</Formik>
							</div>
						</Col>
					</Row>
				</div>
			</section>
		</>
	);
};

export default CollectAiVisibility;
